'use client';

export type ConsentRegion = 'gdpr' | 'ccpa' | 'none';

export interface ConsentRecord {
  version: '1.0';
  timestamp: string; // ISO 8601
  region: ConsentRegion;
  choices: {
    necessary: true;
    analytics: boolean;
    marketing: boolean;
    preferences: boolean;
  };
  userAgent: string;
}

const APP_NAME = 'danny_clans_media';
const CONSENT_KEY = APP_NAME.toLowerCase()
  .replace(/[^a-z0-9]/g, '_')
  .replace(/_+/g, '_') + '_consent_v1';

/**
 * Detect user's region based on browser locale and timezone
 * No external API calls — uses navigator.language and Intl.DateTimeFormat
 */
export function detectRegion(): ConsentRegion {
  if (typeof navigator === 'undefined') return 'gdpr'; // SSR default

  const lang = navigator.language || '';
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';

  const euTZPrefixes = [
    'Europe/',
    'Atlantic/Azores',
    'Atlantic/Canary',
    'Atlantic/Faroe',
    'Atlantic/Madeira',
    'Arctic/Longyearbyen',
  ];

  const usTZPrefixes = ['America/'];

  const euCountryCodes = [
    'AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI',
    'FR', 'GR', 'HR', 'HU', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT',
    'NL', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK', 'GB', 'IS', 'LI',
    'NO', 'CH',
  ];

  const isEU =
    euTZPrefixes.some((p) => tz.startsWith(p)) ||
    euCountryCodes.some((c) => lang.toUpperCase().endsWith('-' + c));

  const isUS =
    usTZPrefixes.some((p) => tz.startsWith(p)) &&
    (lang.startsWith('en-US') || lang.startsWith('en-CA'));

  if (isEU) return 'gdpr';
  if (isUS) return 'ccpa';
  return 'none';
}

/**
 * Save consent record to localStorage
 */
export function saveConsent(record: ConsentRecord): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(record));
  } catch (error) {
    console.error('[Consent] Failed to save consent:', error);
  }
}

/**
 * Retrieve consent record from localStorage
 */
export function getConsent(): ConsentRecord | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('[Consent] Failed to retrieve consent:', error);
    return null;
  }
}

/**
 * Clear consent record from localStorage
 */
export function clearConsent(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(CONSENT_KEY);
  } catch (error) {
    console.error('[Consent] Failed to clear consent:', error);
  }
}

/**
 * Create a new consent record with given choices
 */
export function createConsentRecord(
  region: ConsentRegion,
  choices: Omit<ConsentRecord['choices'], 'necessary'>
): ConsentRecord {
  return {
    version: '1.0',
    timestamp: new Date().toISOString(),
    region,
    choices: {
      necessary: true,
      ...choices,
    },
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
  };
}

/**
 * Log consent action for audit trail
 */
export function logConsentAction(
  action: 'accept_all' | 'reject_all' | 'save_preferences' | 'dismiss_ccpa',
  region: ConsentRegion
): void {
  if (typeof window === 'undefined') return;
  try {
    console.log(`[Consent] ${action} - Region: ${region}`);
  } catch (error) {
    console.error('[Consent] Failed to log consent action:', error);
  }
}
